import React from 'react';
import { renderSafeHtml } from '../../../utils/renderHtml';
import PrecedenceOrderTooltip from './PrecedenceOrderTooltip';
import { DISPLAY_NAME_SOURCE } from '../../../services/api/domains/products/types';
import { GRAY_400, GRAY_500, GRAY_900 } from 'theme/grayScale';

interface SynonymsRadioFieldProps {
  label: string;
  preferredValue: string;
  onChange: (value: string | null) => void;
  isDisabled: boolean;
  tooltipText?: string;
  styles: Record<string, React.CSSProperties>;
  onFocus?: () => void;
  onBlur?: () => void;
  hasValue?: boolean;
  isFocused?: boolean;
  getFloatingLabelStyle?: (
    fieldName: string,
    hasValue: boolean
  ) => React.CSSProperties;
  getFloatingInputStyle?: (
    isTextarea: boolean,
    fieldName: string
  ) => React.CSSProperties;
  /** Pre-resolved display name from the detail endpoint (VIEW mode primary display) */
  displayName?: string;
  /** Source of the resolved display name — drives the source label */
  displayNameSource?: string | null;
}

/**
 * Details-tab field for `preferred_product_name` (the 'synonyms_radio'
 * ModalFieldRenderer field type). VIEW mode shows the pre-resolved display
 * name with its source; EDIT mode shows a plain text input.
 *
 * The Names-tab combo this component used to also render (`inputOnly` +
 * `synonymsOnly`, the floating-label input paired with the full grouped-
 * variant radio list) was extracted into the shared
 * `components/modals/fields/NamesTab.tsx` component (issue #653, Task 16 QA
 * follow-up) so Product and Manufacturer share one Names-tab implementation
 * instead of each modal styling its own. This component now only covers the
 * Details-tab display/edit of the single scalar field.
 */
const SynonymsRadioField: React.FC<SynonymsRadioFieldProps> = ({
  label,
  preferredValue,
  onChange,
  isDisabled,
  styles,
  onFocus,
  onBlur,
  hasValue = false,
  isFocused = false,
  getFloatingLabelStyle,
  getFloatingInputStyle,
  displayName,
  displayNameSource,
}) => {
  const handlePreferredNameChange = (value: string) => {
    onChange(value || null);
  };

  // Show placeholder only when focused and empty (label is floating)
  const shouldShowPlaceholder = isFocused && !preferredValue;

  const SOURCE_LABEL_MAP: Record<string, string> = {
    [DISPLAY_NAME_SOURCE.USER]: 'LGM Product Name',
    [DISPLAY_NAME_SOURCE.ORANGE_BOOK]: 'FDA Orange Book',
    [DISPLAY_NAME_SOURCE.CAS]: 'CAS Registry',
  };

  // VIEW mode: show the resolved display name prominently
  if (isDisabled) {
    const sourceText =
      displayNameSource != null
        ? (SOURCE_LABEL_MAP[displayNameSource] ?? displayNameSource)
        : null;

    return (
      <div style={componentStyles.viewBlock}>
        <div style={componentStyles.viewLabel}>
          {label}
          {sourceText && (
            <span style={componentStyles.sourceSubLabel}>
              <span style={componentStyles.sourceSubLabelPrefix}>
                Sourced from:
              </span>{' '}
              {sourceText}
            </span>
          )}
          <PrecedenceOrderTooltip size={13} />
        </div>
        <div style={componentStyles.viewValue}>
          {displayName ? (
            renderSafeHtml(displayName)
          ) : (
            <em style={componentStyles.missing}>Not set</em>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <p style={componentStyles.editHint}>
        Sets the LGM product name shown across the application. If left blank,
        the name is derived using the precedence order.{' '}
        <PrecedenceOrderTooltip size={12} />
      </p>
      {/* Preferred Product Name Text Input */}
      <div style={styles.floatingFieldContainer}>
        <input
          id="preferred_product_name"
          type="text"
          value={preferredValue || ''}
          onChange={e => handlePreferredNameChange(e.target.value)}
          disabled={isDisabled}
          onFocus={onFocus}
          onBlur={onBlur}
          style={
            getFloatingInputStyle
              ? getFloatingInputStyle(false, 'preferred_product_name')
              : {}
          }
          placeholder={
            shouldShowPlaceholder
              ? 'Type a name, or select from synonyms below'
              : ' '
          }
        />
        <label
          htmlFor="preferred_product_name"
          style={
            getFloatingLabelStyle
              ? getFloatingLabelStyle(
                  'preferred_product_name',
                  hasValue || !!preferredValue
                )
              : {}
          }>
          {label}
        </label>
      </div>
    </>
  );
};

const componentStyles: Record<string, React.CSSProperties> = {
  viewBlock: {
    marginBottom: '16px',
  },
  viewLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontWeight: '500',
    color: GRAY_500,
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  viewValue: {
    fontSize: '15px',
    fontWeight: '500',
    color: GRAY_900,
    lineHeight: '1.4',
    wordBreak: 'break-word',
  },
  missing: {
    color: GRAY_400,
    fontWeight: 'normal',
  },
  sourceSubLabel: {
    fontSize: '11px',
    color: GRAY_500,
    textTransform: 'none' as const,
    letterSpacing: 'normal',
    fontWeight: '400',
  },
  sourceSubLabelPrefix: {
    fontWeight: '600',
    color: GRAY_500,
  },
  editHint: {
    margin: '0 0 12px',
    fontSize: '13px',
    color: GRAY_500,
    lineHeight: '1.5',
  },
};

export default SynonymsRadioField;
